import { useEffect, useMemo, useState } from "react";
import reportApi from "../../../../api/reportApi";
import { useReportFilters } from "../../contexts/ReportFiltersContext";
import EmptyState from "../../components/EmptyState";

const DOW_LABELS = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const HeatmapTab = () => {
  const { shopId, from, to, isRangeValid } = useReportFilters();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shopId || !isRangeValid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportApi
      .getHeatmap({ shopId, from, to })
      .then((res) => {
        if (cancelled) return;
        if (res.status === 1) setRows(res.data || []);
        else setError(res.message || "Không tải được dữ liệu");
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [shopId, from, to, isRangeValid]);

  const { grid, max, peakText, totalMessages } = useMemo(() => {
    const g = Array.from({ length: 7 }, () => Array(24).fill(0));
    let mx = 0;
    let total = 0;
    let peakDow = 0;
    let peakHour = 0;
    rows.forEach((r) => {
      const dow = Number(r.dow);
      const h = Number(r.hour);
      const v = Number(r.messages);
      if (dow >= 0 && dow < 7 && h >= 0 && h < 24) {
        g[dow][h] = v;
        total += v;
        if (v > mx) {
          mx = v;
          peakDow = dow;
          peakHour = h;
        }
      }
    });
    const peak =
      mx > 0
        ? `Cao điểm: ${DOW_LABELS[peakDow]} ${String(peakHour).padStart(2, "0")}:00 (${mx} tin)`
        : null;
    return { grid: g, max: mx, peakText: peak, totalMessages: total };
  }, [rows]);

  if (!shopId) {
    return (
      <div className="p-6">
        <EmptyState title="Chọn Zalo để xem báo cáo" />
      </div>
    );
  }

  const colorFor = (v) => {
    if (v === 0) return "bg-gray-100";
    const ratio = v / (max || 1);
    if (ratio > 0.75) return "bg-blue-600 text-white";
    if (ratio > 0.5) return "bg-blue-500 text-white";
    if (ratio > 0.25) return "bg-blue-400 text-white";
    if (ratio > 0.1) return "bg-blue-300";
    return "bg-blue-100";
  };

  return (
    <div className="p-4 space-y-4">
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">
            Giờ khách nhắn nhiều
          </h2>
          <div className="text-xs text-gray-500 mt-0.5">
            Tổng {totalMessages} tin khách · {peakText || "chưa có dữ liệu"}
          </div>
        </div>
        {loading && <div className="p-6 text-sm text-gray-500">Đang tải...</div>}
        {error && <div className="p-6 text-sm text-red-500">Lỗi: {error}</div>}
        {!loading && !error && totalMessages === 0 && (
          <EmptyState description="Không có tin khách trong khoảng thời gian này" />
        )}
        {!loading && !error && totalMessages > 0 && (
          <div className="overflow-x-auto p-4">
            <div className="inline-block min-w-full">
              <div className="flex gap-0.5 mb-1 pl-8">
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className="w-6 text-[9px] text-center text-gray-400"
                  >
                    {h % 3 === 0 ? String(h).padStart(2, "0") : ""}
                  </div>
                ))}
              </div>
              {grid.map((row, dow) => (
                <div key={dow} className="flex gap-0.5 mb-0.5 items-center">
                  <div className="w-8 text-xs text-gray-500 pr-1">
                    {DOW_LABELS[dow]}
                  </div>
                  {row.map((v, h) => (
                    <div
                      key={h}
                      title={`${DOW_LABELS[dow]} ${String(h).padStart(2, "0")}:00 — ${v} tin`}
                      className={`w-6 h-6 text-[10px] flex items-center justify-center rounded-sm ${colorFor(
                        v,
                      )}`}
                    >
                      {v > 0 && v}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeatmapTab;
